import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import { InputStyles } from './InputStyles';

type Option = {
  label: string;
  value: number | string;
};

interface SelectBoxProps {
  label?: string;
  value: number | string | Option;
  options: Option[];
  isEdit?: boolean;
  disabled?: boolean;
  onChange: (value: Option) => void;
  id?: string;
  name?: string;
  // 스타일 관련 props
  width?: string;
  height?: string;
  fontSize?: string;
  align?: 'left' | 'center' | 'right';
  variant?: 'default' | 'compact';
  compact?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

const SelectContainer = styled.div<{
  width?: string;
}>`
  position: relative;
  width: ${({ width }) => width || '100%'};
  display: inline-block;
`;

const SelectButton = styled.button<{
  disabled?: boolean;
  width?: string;
  height?: string;
  fontSize?: string;
  align?: 'left' | 'center' | 'right';
  $size?: 'xs' | 'sm' | 'md' | 'lg';
  $isOpen?: boolean;
}>`
  text-align: ${({ align = 'center' }) => align};
  width: 100%;
  height: 26px;
  box-sizing: border-box;
  border: ${({ theme }) => theme.borderWidth.md}px solid
    ${({ theme, $isOpen }) =>
      $isOpen ? theme.colors.border.indicator : theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.surface.control.default};
  outline: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin: 0;

  vertical-align: middle;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing[2]}px ${({ theme }) => theme.spacing[4]}px;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  ${({ height }) => height && `height: ${height} !important;`}
  ${({ fontSize }) => fontSize && `font-size: ${fontSize} !important;`}

  &:focus {
    border-color: ${({ theme }) => theme.colors.border.indicator};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.surface.interactive.hover};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.surface.control.disabled};
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.border.interactive.hover};
  }
`;

const SelectValue = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChevronIcon = styled.span<{ $isOpen?: boolean }>`
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid ${({ theme }) => theme.colors.text.primary};
  margin-left: ${({ theme }) => theme.spacing[2]}px;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  flex-shrink: 0;
`;

const DropdownMenu = styled.div<{
  $openUpward?: boolean;
}>`
  position: fixed;
  background: ${({ theme }) => theme.colors.surface.layout.overlay};
  border: ${({ theme }) => theme.borderWidth.md}px solid
    ${({ theme }) => theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  max-height: 200px;
  overflow-y: auto;
  box-sizing: border-box;
`;

const DropdownOption = styled.div<{
  $isSelected?: boolean;
  $size?: 'xs' | 'sm' | 'md' | 'lg';
}>`
  ${({ theme, $size = 'sm' }) => {
    const sizeMap = {
      xs: { height: 20, paddingX: theme.spacing[4], fontSize: theme.fontSize.xs },
      sm: { height: 24, paddingX: theme.spacing[6], fontSize: theme.fontSize.md },
      md: { height: 32, paddingX: theme.spacing[6], fontSize: theme.fontSize.md },
      lg: { height: 36, paddingX: theme.spacing[7], fontSize: theme.fontSize.md },
    };
    const config = sizeMap[$size];
    return `
      padding: 0 ${config.paddingX}px;
      font-size: ${config.fontSize}px;
      line-height: ${config.height - 2}px;
      min-height: ${config.height}px;
    `;
  }}
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;

  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.surface.interactive.selectedContent : 'transparent'};
  color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.text.emphasis : theme.colors.text.primary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface.interactive.hover};
  }

  &:first-of-type {
    border-top-left-radius: ${({ theme }) => theme.borderRadius.md}px;
    border-top-right-radius: ${({ theme }) => theme.borderRadius.md}px;
  }

  &:last-of-type {
    border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}px;
    border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}px;
  }
`;

interface DropdownPosition {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
}

export const SelectBox: React.FC<SelectBoxProps> = ({
  label,
  value,
  options,
  isEdit,
  disabled: disabledProp = false,
  onChange,
  id,
  name,
  width,
  height,
  fontSize,
  align,
  compact,
  size = 'sm',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition & { openUpward: boolean }>({
    top: 0,
    left: 0,
    width: 0,
    openUpward: false,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLDivElement>(null);

  // value가 Option 객체인지 확인하고 적절한 값 추출
  const safeValue =
    typeof value === 'object' && value !== null && 'value' in value ? value.value : value;

  // 선택된 옵션 찾기 (엄격한 비교)
  const selectedOption = options.find(opt => {
    // 타입이 같으면 직접 비교
    if (typeof opt.value === typeof safeValue) {
      return opt.value === safeValue;
    }
    // 타입이 다르면 String으로 변환해서 비교
    return String(opt.value) === String(safeValue);
  });

  // 디버깅: 선택된 옵션이 없을 때 경고
  if (safeValue !== undefined && safeValue !== null && !selectedOption && options.length > 0) {
    console.warn('[SelectBox] Selected option not found', {
      value,
      safeValue,
      options,
      label,
    });
  }

  // 고유 ID 생성 (id가 없으면 label 기반으로 생성)
  const selectId = id || (label ? `select-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);

  // 드롭다운 위치 계산 (portal 렌더링을 위해 버튼의 절대 위치 측정)
  const calculatePosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = Math.min(options.length * 32, 200);
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < dropdownHeight + 8 && spaceAbove > spaceBelow;

    setDropdownPos({
      left: rect.left,
      width: rect.width,
      openUpward,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  // 클릭 외부 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    // 드롭다운 내부 스크롤은 닫기 대상에서 제외 (바깥 스크롤만 위치가 어긋나므로 닫음)
    const handleScroll = (event: Event) => {
      if (dropdownRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // 선택 항목이 목록 밖에 있으면 보이도록 스크롤 (드롭다운을 열 때 + 화살표 키 이동 시)
  useEffect(() => {
    if (!isOpen) return;
    selectedOptionRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, safeValue]);

  // 키보드 네비게이션
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isEdit) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        setIsOpen(!isOpen);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          const currentIndex = options.findIndex(opt => opt.value === safeValue);
          const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
          const nextOption = options[nextIndex];
          if (nextOption) onChange(nextOption);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          const currentIndex = options.findIndex(opt => opt.value === safeValue);
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
          const prevOption = options[prevIndex];
          if (prevOption) onChange(prevOption);
        }
        break;
    }
  };

  const handleOptionClick = (option: Option) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleButtonClick = () => {
    if (isEdit && !disabled) {
      if (!isOpen) calculatePosition();
      setIsOpen(!isOpen);
    }
  };

  const disabled = disabledProp || !isEdit;

  const dropdown = isOpen
    ? ReactDOM.createPortal(
        <DropdownMenu
          ref={dropdownRef}
          role="listbox"
          style={{
            left: dropdownPos.left,
            width: dropdownPos.width,
            ...(dropdownPos.openUpward
              ? { bottom: dropdownPos.bottom }
              : { top: dropdownPos.top }),
          }}
        >
          {options.map(opt => {
            const isSelected =
              typeof opt.value === typeof safeValue
                ? opt.value === safeValue
                : String(opt.value) === String(safeValue);
            return (
              <DropdownOption
                key={opt.value}
                ref={isSelected ? selectedOptionRef : undefined}
                $isSelected={isSelected}
                $size={size}
                onClick={() => handleOptionClick(opt)}
                role="option"
                aria-selected={isSelected}
              >
                {opt.label}
              </DropdownOption>
            );
          })}
        </DropdownMenu>,
        document.body
      )
    : null;

  const select = (
    <SelectContainer ref={containerRef} width={width}>
      <SelectButton
        ref={buttonRef}
        id={selectId}
        name={name || selectId}
        disabled={disabled}
        width={width}
        height={height}
        fontSize={fontSize}
        align={align}
        $size={size}
        $isOpen={isOpen}
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label || 'Select option'}
      >
        <SelectValue>{selectedOption?.label || ''}</SelectValue>
        <ChevronIcon $isOpen={isOpen} />
      </SelectButton>
      {dropdown}
    </SelectContainer>
  );

  // label이 없으면 wrapper 없이 바로 select 반환 (TopBar 용도)
  if (!label) {
    return select;
  }

  // label이 있으면 기존 wrapper 구조 유지
  return (
    <InputStyles.InputContainer compact={compact}>
      <InputStyles.StyledInputRow>
        <InputStyles.Label htmlFor={selectId}>{label}</InputStyles.Label>
        <InputStyles.InputWrapper compact={compact}>{select}</InputStyles.InputWrapper>
      </InputStyles.StyledInputRow>
    </InputStyles.InputContainer>
  );
};
